import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { doc, getDoc, updateDoc, arrayUnion, serverTimestamp, setDoc, collectionGroup, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/authContext';
import LoadingSpinner from '../components/LoadingSpinner';

const AcceptInvitation = () => {
  const [invitation, setInvitation] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();

  useEffect(() => {
    const loadInvitation = async () => {
      try {
        const urlParams = new URLSearchParams(location.search);
        const inviteId = urlParams.get('inviteId');
        const orgId = urlParams.get('orgId');
        
        if (!inviteId || !orgId) {
          setError('No invitation ID or organization ID found');
          setIsLoading(false);
          return;
        }

        // Get invitation details from the correct subcollection path
        let inviteRef = null;
        let inviteSnap = null;
        let inviteData = null;
        let foundOrgId = orgId;

        if (orgId) {
          // Try the new subcollection path first
          inviteRef = doc(db, 'organizations', orgId, 'invitations', inviteId);
          inviteSnap = await getDoc(inviteRef);
          if (inviteSnap.exists()) {
            inviteData = inviteSnap.data();
            foundOrgId = orgId;
          }
        }

        // If not found and orgId is missing (backward compatibility for old links)
        if (!inviteData && !orgId) {
          try {
            // Try to find invitation across all organizations using collection group query
            const invitationsQuery = query(
              collectionGroup(db, 'invitations'),
              where('__name__', '==', inviteId)
            );
            const snapshot = await getDocs(invitationsQuery);
            
            if (!snapshot.empty) {
              const inviteDoc = snapshot.docs[0];
              inviteData = inviteDoc.data();
              // Extract orgId from the path: organizations/{orgId}/invitations/{inviteId}
              const pathParts = inviteDoc.ref.path.split('/');
              foundOrgId = pathParts[1]; // organizations/{orgId}
              inviteRef = inviteDoc.ref;
            } else {
              // Fallback: Try old organizationInvites collection
              const oldInviteRef = doc(db, 'organizationInvites', inviteId);
              const oldInviteSnap = await getDoc(oldInviteRef);
              if (oldInviteSnap.exists()) {
                inviteData = oldInviteSnap.data();
                foundOrgId = inviteData.organizationId;
                inviteRef = oldInviteRef;
              }
            }
          } catch (collectionGroupError) {
            console.error('Error searching for invitation:', collectionGroupError);
            // Try old collection as fallback
            try {
              const oldInviteRef = doc(db, 'organizationInvites', inviteId);
              const oldInviteSnap = await getDoc(oldInviteRef);
              if (oldInviteSnap.exists()) {
                inviteData = oldInviteSnap.data();
                foundOrgId = inviteData.organizationId;
                inviteRef = oldInviteRef;
              }
            } catch (oldError) {
              console.error('Error checking old invitation collection:', oldError);
            }
          }
        }

        if (!inviteData || !inviteRef) {
          setError('Invitation not found');
          setIsLoading(false);
          return;
        }
        
        if (inviteData.status !== 'pending') {
          setError('This invitation has already been processed');
          setIsLoading(false);
          return;
        }

        // Check if user is already a member
        const userOrgRef = doc(db, 'users', currentUser.uid, 'organizations', foundOrgId);
        const userOrgSnap = await getDoc(userOrgRef);
        
        if (userOrgSnap.exists()) {
          setError('You are already a member of this organization');
          setIsLoading(false);
          return;
        }

        // Get organization details
        const orgRef = doc(db, 'organizations', foundOrgId);
        const orgSnap = await getDoc(orgRef);

        if (!orgSnap.exists()) {
          setError('Organization not found');
          setIsLoading(false);
          return;
        }

        setInvitation({ ...inviteData, organizationId: foundOrgId });
        setOrganization(orgSnap.data());
        
        // Update URL to include orgId if it was missing
        if (!orgId && foundOrgId) {
          const newUrl = `${window.location.pathname}?orgId=${foundOrgId}&inviteId=${inviteId}`;
          window.history.replaceState({}, '', newUrl);
        }
        
      } catch (error) {
        console.error('Error loading invitation:', error);
        setError('Failed to load invitation details');
      } finally {
        setIsLoading(false);
      }
    };

    if (currentUser) {
      loadInvitation();
    }
  }, [location.search, currentUser]);

  const handleAccept = async () => {
    setIsProcessing(true);
    setError(null);

    try {
      const urlParams = new URLSearchParams(location.search);
      const inviteId = urlParams.get('inviteId');
      let orgId = urlParams.get('orgId') || invitation?.organizationId;

      // If orgId is still missing, try to find it
      if (!orgId && inviteId) {
        try {
          const invitationsQuery = query(
            collectionGroup(db, 'invitations'),
            where('__name__', '==', inviteId)
          );
          const snapshot = await getDocs(invitationsQuery);
          if (!snapshot.empty) {
            const pathParts = snapshot.docs[0].ref.path.split('/');
            orgId = pathParts[1];
          }
        } catch (error) {
          console.error('Error finding orgId for invitation:', error);
        }
      }

      if (!orgId || !inviteId) {
        setError('Missing organization or invitation ID');
        setIsProcessing(false);
        return;
      }

      // Add user to the organization's members and users (both so admin Members list shows them)
      const memberPayload = {
        role: invitation.role || 'member',
        status: 'active',
        joinedAt: serverTimestamp(),
        name: currentUser.displayName || currentUser.email || 'Member',
        email: currentUser.email || '',
        appID: currentUser.uid
      };
      await setDoc(doc(db, 'organizations', orgId, 'members', currentUser.uid), memberPayload);

      // Add the org to the user's `activeOrgs` array
      await updateDoc(doc(db, 'users', currentUser.uid), {
        activeOrgs: arrayUnion(orgId)
      });

      // Update user's subcollection for this org
      await setDoc(doc(db, 'users', currentUser.uid, 'organizations', orgId), {
        role: invitation.role || 'member',
        status: 'active',
        joinedAt: serverTimestamp()
      }, { merge: true });

      // Update invite status in the correct subcollection path
      const inviteRef = doc(db, 'organizations', orgId, 'invitations', inviteId);
      await updateDoc(inviteRef, {
        status: 'accepted',
        acceptedAt: serverTimestamp(),
        acceptedBy: currentUser.uid
      });

      setSuccess(true);
      
      // Redirect after a short delay
      setTimeout(() => {
        navigate('/my-community');
      }, 2000);

    } catch (error) {
      console.error('Error accepting invitation:', error);
      setError('Failed to accept invitation. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDecline = () => {
    navigate('/dashboard');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-xl font-bold text-gray-900 mb-4">Invitation Error</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition-colors"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <div className="text-green-500 text-6xl mb-4">✅</div>
          <h2 className="text-xl font-bold text-gray-900 mb-4">Welcome!</h2>
          <p className="text-gray-600 mb-6">
            You've successfully joined <strong>{organization.name}</strong>!
          </p>
          <p className="text-sm text-gray-500">Redirecting to your community...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
      <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full">
        <div className="text-center mb-6">
          <div className="text-blue-500 text-6xl mb-4">📧</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Organization Invitation</h2>
          <p className="text-gray-600">You've been invited to join an organization</p>
        </div>

        {organization && (
          <div className="bg-gray-50 rounded-lg p-6 mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">{organization.name}</h3>
            {organization.description && (
              <p className="text-gray-600 text-sm mb-3">{organization.description}</p>
            )}
            {organization.location && (
              <p className="text-gray-500 text-sm">📍 {organization.location}</p>
            )}
          </div>
        )}

        <div className="space-y-3">
          <button
            onClick={handleAccept}
            disabled={isProcessing}
            className="w-full bg-blue-600 text-white py-3 px-4 rounded-md hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {isProcessing ? 'Accepting...' : 'Accept Invitation'}
          </button>
          
          <button
            onClick={handleDecline}
            disabled={isProcessing}
            className="w-full bg-gray-200 text-gray-700 py-3 px-4 rounded-md hover:bg-gray-300 transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed"
          >
            Decline
          </button>
        </div>

        <p className="text-xs text-gray-500 text-center mt-4">
          By accepting, you'll be added as a member of this organization and gain access to its community features.
        </p>
      </div>
    </div>
  );
};

export default AcceptInvitation; 